import React, { useMemo } from 'react';
import memoizeOne from 'memoize-one';

import { ErpSessionDetailResponse, VirtualPackErpResponse } from 'com.cogvio.pricemonitor.api';
import { calculateModelAverage } from './PriceBadgeAverage';
import { calculateModelRevenue } from './PriceBadgeRevenue';

import { BASE_SCENARIO_ID } from './ScenarioSelect';
import { ChartBar } from './ChartBar';
import { ChartComparison } from './ChartComparison';
import { ScenarioSelect } from './ScenarioSelect';
import { PriceSummary } from './PriceSummary';
import { SpinningLoadingIcon } from '~/libs/ui/atoms/SpinningLoadingIcon';
import { ERPApiResult } from '~/libs/infrastructure/schemas/erp';
import { formatToCompactNumber } from '~/libs/utils/format/number';

export interface ErpScenarioComparisonResults {
	data: VirtualPackErpResponse;
	parseErpResults: ERPApiResult;
}

const options = [
	{ id: null, name: 'Choose scenario' },
	{ id: BASE_SCENARIO_ID, name: 'Base scenario' },
];
const BAR_CHART_COLORS = ['rgba(38, 41, 85, 0.8)'];

export const pickScenario = memoizeOne(
	<T extends { id: string | null; name?: string }>(scenarioId: string | null | undefined, scenarios: T[]) =>
		scenarioId ? scenarios.find((candidate) => candidate.id === scenarioId) : null
);

interface ScenarioComparisonProps {
	currency: string;
	parseErpResults: ERPApiResult;
	erpSessionResult: ErpSessionDetailResponse;
	leftSelectedScenarioId: string;
	leftScenarioResult: ErpScenarioComparisonResults;
	leftScenarioLoading: boolean;
	rightSelectedScenarioId: string;
	rightScenarioResult: ErpScenarioComparisonResults;
	rightScenarioLoading: boolean;
	onScenarioSelect: (value: string, type: string) => void;
	onScenarioSwitch: () => void;
	onDataAddRequest?: () => void;
}

export function ScenarioComparison({
	currency,
	parseErpResults,
	erpSessionResult,
	leftSelectedScenarioId,
	leftScenarioResult,
	leftScenarioLoading,
	rightSelectedScenarioId,
	rightScenarioResult,
	rightScenarioLoading,
	onScenarioSelect,
	onScenarioSwitch,
	onDataAddRequest,
}: ScenarioComparisonProps) {
	const apiScenarios = useMemo(() => erpSessionResult.erpSession.scenarios || [], [erpSessionResult]);
	const scenarios = useMemo(() => options.concat(apiScenarios), [apiScenarios]);
	const isDefaultSelected = leftSelectedScenarioId === BASE_SCENARIO_ID;

	const handleLeftSelectedScenarioId = (value: string) => {
		onScenarioSelect(value, 'left');
	};
	const handleRightSelectedScenarioId = (value: string) => {
		onScenarioSelect(value, 'right');
	};

	return (
		<div className='px-10 bg-white'>
			<div className='pt-8 pb-20 w-full max-w-7xl mx-auto'>
				<h2 className='mb-1 font-display text-h2-head font-semibold text-cPurple-600'>Comparison</h2>
				<div className='relative py-4 mx-auto max-w-xl w-full flex justify-between'>
					<div className='absolute left-1/2 top-0 w-px h-full bg-gray-200'>
						<button
							className='absolute left-0 top-5 -ml-3 text-cPurple-600 text-opacity-60 cursor-pointer transition hover:text-opacity-100'
							disabled={!rightSelectedScenarioId}
							title='Switch comparison'
							onClick={onScenarioSwitch}
						>
							<svg
								className='w-6 h-6'
								fill='none'
								stroke='currentColor'
								strokeWidth='1.5'
								viewBox='0 0 24 24'
								xmlns='http://www.w3.org/2000/svg'
							>
								<path
									d='M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5'
									strokeLinecap='round'
									strokeLinejoin='round'
								></path>
							</svg>
						</button>
					</div>
					<div className='w-1/2 pr-4'>
						<div className='w-full pb-2'>
							<ScenarioSelect
								disabledScenarioId={rightSelectedScenarioId}
								scenarios={scenarios}
								selectedScenarioId={leftSelectedScenarioId}
								onScenarioSelect={handleLeftSelectedScenarioId}
							/>
						</div>
						<div className='text-right -mr-1'>
							<PriceSummary
								currency={currency}
								monthData={leftScenarioResult.data.monthData}
								cumulativeRevenues={leftScenarioResult.data.stats.cumulativeRevenues}
								type='small'
							/>
						</div>
					</div>
					<div className='w-1/2 pl-4'>
						<div className='w-full pb-2'>
							<ScenarioSelect
								disabledScenarioId={leftSelectedScenarioId}
								scenarios={scenarios}
								selectedScenarioId={rightSelectedScenarioId}
								onScenarioSelect={handleRightSelectedScenarioId}
							/>
						</div>
						{rightSelectedScenarioId && (
							<div className=''>
								<PriceSummary
									currency={currency}
									monthData={rightScenarioResult.data.monthData}
									cumulativeRevenues={rightScenarioResult.data.stats.cumulativeRevenues}
									type='small'
								/>
							</div>
						)}
					</div>
				</div>
				{leftSelectedScenarioId && rightSelectedScenarioId && (
					<div className='relative py-4 text-center'>
						{(leftScenarioLoading || rightScenarioLoading) && (
							<div className='absolute left-0 top-0 flex items-center justify-center w-full h-full bg-white bg-opacity-80 z-20'>
								<SpinningLoadingIcon />
							</div>
						)}
						<div className='mx-auto w-full max-w-xl'>
							<table cellPadding='0' cellSpacing='0' width='100%'>
								<thead>
									<tr className='font-display text-md border-t-2 border-b-2 border-gray-200 bg-gray-100 bg-opacity-60'>
										<th className='px-3 py-1.5 text-left'>Scenario:</th>
										<th className='px-3 py-1.5 text-right'>Revenue (4Y)</th>
										<th className='px-3 py-1.5 text-right'>AVG price (in 4Y)</th>
									</tr>
								</thead>
								<tbody>
									<tr className='border-b border-gray-200'>
										<th className='px-3 py-2 font-display text-label font-semibold text-gray-500 text-left'>
											{pickScenario(leftSelectedScenarioId, scenarios)?.name}:
										</th>
										<td className='px-3 py-2 font-roboto text-label font-semibold text-cPurple-600 text-right tabular-nums'>
											{currency}&nbsp;
											{formatToCompactNumber(calculateModelRevenue(leftScenarioResult.data.stats.cumulativeRevenues))}
										</td>
										<td className='px-3 py-2 font-roboto text-label font-semibold text-cPurple-600 text-right tabular-nums'>
											{formatToCompactNumber(calculateModelAverage(leftScenarioResult.data.monthData))}&nbsp;
											{currency}
											&nbsp;/&nbsp;SKU
										</td>
									</tr>
									{rightScenarioResult && (
										<tr className='border-b border-gray-200'>
											<th className='px-3 py-2 font-display text-label font-semibold text-gray-500 text-left'>
												{pickScenario(rightSelectedScenarioId, scenarios)?.name}:
											</th>
											<td className='px-3 py-2 font-roboto text-label font-semibold text-cPurple-600 text-right tabular-nums'>
												{currency}&nbsp;
												{formatToCompactNumber(
													calculateModelRevenue(rightScenarioResult.data.stats.cumulativeRevenues)
												)}
											</td>
											<td className='px-3 py-2 font-roboto text-label font-semibold text-cPurple-600 text-right tabular-nums'>
												{formatToCompactNumber(calculateModelAverage(rightScenarioResult.data.monthData))}&nbsp;
												{currency}
												&nbsp;/&nbsp;SKU
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
				)}
				<div className='relative py-4 flex justify-between'>
					<div className='w-1/2 pr-4'>
						<div className='relative'>
							<h4 className='mb-1 font-display px-4 text-h4 font-semibold text-cPurple-600'>
								{pickScenario(leftSelectedScenarioId, scenarios)?.name}
							</h4>
							{leftScenarioLoading && (
								<div className='absolute left-0 top-0 flex items-center justify-center w-full h-full bg-white bg-opacity-80 z-30'>
									<SpinningLoadingIcon />
								</div>
							)}
							<ChartBar
								colors={BAR_CHART_COLORS}
								currency={currency}
								parseErpResults={leftScenarioResult.parseErpResults}
								onDataAddRequest={leftSelectedScenarioId === BASE_SCENARIO_ID ? onDataAddRequest : undefined}
							/>
						</div>
					</div>
					<div className='w-1/2 pl-4'>
						<div className='relative'>
							<h4 className='mb-1 font-display px-4 text-h4 font-semibold text-cPurple-600'>
								{pickScenario(rightSelectedScenarioId, scenarios)?.name || '\u00A0'}
							</h4>
							{rightScenarioLoading && (
								<div className='absolute left-0 top-0 flex items-center justify-center w-full h-full bg-white bg-opacity-80 z-30'>
									<SpinningLoadingIcon />
								</div>
							)}
							<ChartComparison
								parseErpResults={parseErpResults}
								currency={currency}
								leftModelResult={leftScenarioResult.parseErpResults}
								rightModelResult={rightScenarioResult.parseErpResults}
								scenarios={scenarios}
							/>
							{!rightSelectedScenarioId && (
								<div className='absolute left-0 bottom-0 flex items-center justify-center w-full h-full bg-white bg-opacity-80 text-center z-20'>
									<div className='p-4 bg-white bg-opacity-100'>
										<h3 className='mb-0.5 font-display text-base font-medium text-cPurple-600 tracking-wider'>
											Choose scenario for compare
										</h3>
										<p className='mb-2 font-display text-label leading-normal text-gray-500 tracking-wide'>
											In the list above, select a scenario
											<br />
											to enter the comparison calculations.
										</p>
										<ScenarioSelect
											disabledScenarioId={isDefaultSelected ? BASE_SCENARIO_ID : leftSelectedScenarioId}
											scenarios={scenarios}
											selectedScenarioId={''}
											onScenarioSelect={handleRightSelectedScenarioId}
										/>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
				<div className='flex justify-between'>
					<div className='w-1/2 pr-4'>
						<div className='mb-2.5 flex items-center justify-start'>
							<i className='relative top-0 -mt-px block mr-1.5 w-2.5 h-2.5 rounded-sm bg-cPurple-800'></i>
							<p className='font-display text-labelsmall font-medium leading-none text-gray-600 tracking-wider'>
								first selected scenario (left): revenue after 4 years
							</p>
						</div>
					</div>
					<div className='w-1/2 pl-4'>
						<div className='mb-2.5 flex items-center justify-start'>
							<i className='relative top-0 -mt-px block mr-1.5 w-2.5 h-2.5 rounded-sm bg-cEmerald-500'></i>
							<p className='font-display text-labelsmall font-medium leading-none text-gray-600 tracking-wider'>
								second selected scenario (right): revenue after 4 years — higher than in first selected scenario
							</p>
						</div>
						<div className='mb-2.5 flex items-center justify-start'>
							<i className='relative top-0 -mt-px block mr-1.5 w-2.5 h-2.5 rounded-sm bg-red-700'></i>
							<p className='font-display text-labelsmall font-medium leading-none text-gray-600 tracking-wider'>
								second selected scenario (right): revenue after 4 years — lower than in first selected scenario
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
